import { QuoteError, Validation } from '~/lib/api/smartsend/types';
import { captureWarning } from '~/lib/metrics';
import { QuoteErrorCode } from '../../components/smartsend/item/QuoteErrors';
import { onlyUnique } from './ArrayHelper';

/**
 * Reports a shipment that quoting refused to price.
 *
 * These refusals are invisible to monitoring on their own. Quoting answers a request it cannot price with
 * a successful response carrying the reason, so nothing throws, nothing is captured, and the row simply
 * renders its message. A restriction that blocks every service on an account therefore looks identical to
 * an ordinary day until somebody reports it.
 *
 * Only the normalised code is tagged. The reported `Detail` quotes the offending value back - postcodes
 * among them - so it is deliberately left out, and the tag stays aggregatable as a result.
 */

/**
 * One report per distinct reason per page load. A blocked shipment is re-quoted on every refresh and every
 * edit, so reporting each occurrence would bury the signal in repeats of a fault already known. Exact
 * counts belong to the quoting event stream, which records every request either way.
 */
const reported = new Set<string>();

/** Test seam - the set is module state, which would otherwise leak between cases. */
export const ResetReportedQuoteFailures = () => reported.clear();

export function ReportQuoteFailure(validation: Validation | undefined): void {
  const quoteErrors = validation?.QuoteState?.Errors;

  // Our own errors decide whether the customer is blocked; quoting's decide whether we can say why. With
  // no reason attached there is nothing to aggregate on, and SERVICE-UNAVAILABLE alone says only that a
  // price was not returned.
  if (!validation?.Errors?.length || !quoteErrors?.length) {
    return;
  }

  const codes = quoteErrors
    .map((error: QuoteError) => QuoteErrorCode(error?.Type))
    .filter((code) => !!code)
    .filter(onlyUnique)
    .sort()
    .join(',');

  if (!codes || reported.has(codes)) {
    return;
  }

  reported.add(codes);

  captureWarning('SmartSend quote returned no price', {
    section: 'smartsend-quote',
    'quote-error-codes': codes,
    'item-error': validation.Errors[0]
  });
}
